import { css } from 'lit';

/**
 * Toolbar chip — a self-contained, finger-sized icon button for the mobile
 * HUD bars (loadout, scoreboard, and other touch-only toolbar actions).
 *
 * 44×44 meets the touch-target floor, and each chip carries its own rounded
 * background so the bars read as discrete, gap-separated tap targets rather
 * than one cramped strip (the strip is the desktop menu-toolbar's look).
 *
 * Usage: add to a component's styles array and put `class="toolbar-chip"` on
 * the button. The icon is an `<img>`; it's recoloured white to match the
 * other toolbar glyphs.
 *
 * ```
 * static override styles = [tokens, toolbarChip, css`...`];
 * ```
 */
export const toolbarChip = css`
  .toolbar-chip {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    background-color: var(--color-bg-chip);
    color: var(--color-text-tooltip);
    border: none;
    border-radius: var(--radius-md);
    cursor: pointer;
    outline: none;
    /* A <button> doesn't inherit font-family — the UA stylesheet gives it
       Arial — so any text on a chip (loadout's upgrade-count badge) needs the
       app font stated here. */
    font-family: var(--font-family);
    /* Kill iOS's double-tap-zoom hesitation and the grey tap flash. */
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    /* The icon is an <img>, so a held tap mustn't raise iOS's "save image"
       callout or start a text selection. Here rather than on each host, so
       every chip carries it. */
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }

  .toolbar-chip img {
    width: 22px;
    height: 22px;
    filter: brightness(0) invert(1);
    opacity: 0.85;
    /* Block the mouse-drag ghost image of the glyph. */
    -webkit-user-drag: none;
  }

  /* Hover-only — gated so iOS sticky-hover doesn't trap the chip in the lit
     state after a tap. */
  @media (hover: hover) {
    .toolbar-chip:hover {
      background-color: var(--color-bg-selector-hover);
    }
    .toolbar-chip:hover img {
      opacity: 1;
    }
  }

  .toolbar-chip:active {
    background-color: var(--color-bg-selector-hover);
  }
`;
